/**
 * Audit Trail Page — table layout with DataToolbar, DataPagination,
 * and Framer Motion staggered entrance animations.
 */
import { useRef, useCallback } from 'react'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { Table, TableCell, TableHeader, TableRow } from '@/components/ui/table'
import { api } from '@/lib/api-client'
import type { AuditTrail } from '@/features/audit/types'
import { Clock, User, FileText } from 'lucide-react'
import { format } from 'date-fns'
import { motion } from 'framer-motion'
import { useListState } from '@/hooks/use-list-state'
import { DataToolbar } from '@/components/ui/data-toolbar'
import { DataPagination } from '@/components/ui/data-pagination'

// ── Badge helpers ─────────────────────────────────────────────────────────

const ACTION_STYLES: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; className?: string }> = {
    created: { variant: 'default', className: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30' },
    updated: { variant: 'secondary', className: 'bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/30' },
    deleted: { variant: 'destructive', className: 'bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30' },
    answered: { variant: 'outline', className: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30' },
}

const ENTITY_STYLES: Record<string, { className: string }> = {
    organization: { className: 'bg-indigo-500/15 text-indigo-700 dark:text-indigo-400 border-indigo-500/30' },
    assessment: { className: 'bg-violet-500/15 text-violet-700 dark:text-violet-400 border-violet-500/30' },
    answer: { className: 'bg-teal-500/15 text-teal-700 dark:text-teal-400 border-teal-500/30' },
}

// ── Animation variants ────────────────────────────────────────────────────

const pageVariants = {
    hidden: { opacity: 0, y: 16 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.4, ease: [0.25, 0.1, 0.25, 1] as [number, number, number, number] },
    },
}

const tableVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.03 } },
}

const rowVariants = {
    hidden: { opacity: 0, x: -8 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.2 } },
}

// ── Resizable column header ───────────────────────────────────────────────

function ResizableHead({
    children,
    defaultWidth,
    minWidth = 60,
}: {
    children: React.ReactNode
    defaultWidth?: number
    minWidth?: number
}) {
    const thRef = useRef<HTMLTableCellElement>(null)

    const onMouseDown = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault()
            const th = thRef.current
            if (!th) return

            const startX = e.clientX
            const startW = th.offsetWidth

            const onMove = (ev: MouseEvent) => {
                const newW = Math.max(minWidth, startW + ev.clientX - startX)
                th.style.width = `${newW}px`
            }

            const onUp = () => {
                document.removeEventListener('mousemove', onMove)
                document.removeEventListener('mouseup', onUp)
                document.body.style.cursor = ''
                document.body.style.userSelect = ''
            }

            document.body.style.cursor = 'col-resize'
            document.body.style.userSelect = 'none'
            document.addEventListener('mousemove', onMove)
            document.addEventListener('mouseup', onUp)
        },
        [minWidth],
    )

    return (
        <th
            ref={thRef}
            className="h-10 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 relative select-none"
            style={{ width: defaultWidth ? `${defaultWidth}px` : undefined }}
        >
            {children}
            {/* Drag handle */}
            <span
                onMouseDown={onMouseDown}
                className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-primary/30 active:bg-primary/50 transition-colors"
            />
        </th>
    )
}

// ── Component ─────────────────────────────────────────────────────────────

export function AuditTrailPage() {
    const listState = useListState({
        defaultPerPage: 25,
        defaultSortBy: 'created_at',
        defaultSortOrder: 'desc',
    })

    // Build query params: merge listState.queryParams with filter keys
    const queryParams = {
        ...listState.queryParams,
        action: listState.filters.action as string | undefined,
        entity_type: listState.filters.entity_type as string | undefined,
    }

    const { data: auditResponse, isLoading } = useQuery({
        queryKey: ['admin', 'audit-trail', queryParams],
        queryFn: () => api.audit.getAll(queryParams as any),
        placeholderData: keepPreviousData,
    })

    // Extract items + pagination from response
    const rawData = auditResponse?.data?.data ?? auditResponse?.data ?? auditResponse
    const auditEntries: AuditTrail[] = rawData?.items ?? rawData?.entries ?? (Array.isArray(rawData) ? rawData : [])
    const pagination = rawData?.pages != null
        ? { total: rawData.total, page: rawData.page, per_page: rawData.per_page, pages: rawData.pages }
        : null

    if (isLoading && !auditResponse) {
        return (
            <div className="flex items-center justify-center h-64">
                <LoadingSpinner />
            </div>
        )
    }

    return (
        <motion.div
            className="space-y-6"
            variants={pageVariants}
            initial="hidden"
            animate="visible"
        >
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold">Audit Trail</h1>
                <p className="text-muted-foreground">Track all changes to organizations and assessments</p>
            </div>

            {/* Toolbar: search + filters + sort + per-page */}
            <DataToolbar
                listState={listState}
                searchPlaceholder="Search audit entries..."
                sortOptions={[
                    { label: 'Date', value: 'created_at' },
                    { label: 'Action', value: 'action' },
                    { label: 'Entity', value: 'entity_type' },
                ]}
                filters={[
                    {
                        key: 'action',
                        label: 'Action',
                        options: [
                            { label: 'Created', value: 'created' },
                            { label: 'Updated', value: 'updated' },
                            { label: 'Deleted', value: 'deleted' },
                            { label: 'Answered', value: 'answered' },
                        ],
                    },
                    {
                        key: 'entity_type',
                        label: 'Entity Type',
                        options: [
                            { label: 'Organization', value: 'organization' },
                            { label: 'Assessment', value: 'assessment' },
                            { label: 'Answer', value: 'answer' },
                        ],
                    },
                ]}
                totalItems={pagination?.total ?? auditEntries.length}
                perPageOptions={[10, 25, 50, 100]}
            />

            {/* Table */}
            <Card>
                <CardContent className="p-0">
                    {auditEntries.length === 0 ? (
                        <motion.div
                            className="text-center py-16"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                        >
                            <FileText className="h-12 w-12 mx-auto text-muted-foreground/40 mb-3" />
                            <p className="text-muted-foreground">No audit trail entries found.</p>
                            <p className="text-sm text-muted-foreground/60 mt-1">Try adjusting your search or filters.</p>
                        </motion.div>
                    ) : (
                        <Table style={{ tableLayout: 'fixed', width: '100%' }}>
                            <TableHeader>
                                <TableRow>
                                    <ResizableHead defaultWidth={160}>User</ResizableHead>
                                    <ResizableHead defaultWidth={100}>Action</ResizableHead>
                                    <ResizableHead defaultWidth={120}>Entity</ResizableHead>
                                    <ResizableHead defaultWidth={100}>Entity ID</ResizableHead>
                                    <ResizableHead>Description</ResizableHead>
                                    <ResizableHead defaultWidth={160}>Date</ResizableHead>
                                </TableRow>
                            </TableHeader>
                            <motion.tbody
                                variants={tableVariants}
                                initial="hidden"
                                animate="visible"
                            >
                                {auditEntries.map((entry) => {
                                    const actionStyle = ACTION_STYLES[entry.action] ?? { variant: 'outline' as const }
                                    const entityStyle = ENTITY_STYLES[entry.entity_type] ?? {}

                                    return (
                                        <motion.tr
                                            key={entry.id}
                                            variants={rowVariants}
                                            className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted"
                                        >
                                            {/* User */}
                                            <TableCell>
                                                <div className="flex items-center gap-1.5 min-w-0">
                                                    <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                                        <User className="h-3 w-3 text-primary" />
                                                    </div>
                                                    <div className="min-w-0">
                                                        <div className="text-xs font-medium overflow-hidden text-ellipsis whitespace-nowrap">{entry.user_name}</div>
                                                        <div className="text-[10px] text-muted-foreground overflow-hidden text-ellipsis whitespace-nowrap">{entry.user_email}</div>
                                                    </div>
                                                </div>
                                            </TableCell>

                                            {/* Action */}
                                            <TableCell>
                                                <Badge
                                                    variant={actionStyle.variant}
                                                    className={`text-[10px] uppercase tracking-wider font-semibold ${actionStyle.className ?? ''}`}
                                                >
                                                    {entry.action}
                                                </Badge>
                                            </TableCell>

                                            {/* Entity Type */}
                                            <TableCell>
                                                <Badge
                                                    variant="outline"
                                                    className={`text-[10px] ${entityStyle.className ?? ''}`}
                                                >
                                                    {entry.entity_type || 'unknown'}
                                                </Badge>
                                            </TableCell>

                                            {/* Entity ID */}
                                            <TableCell className="text-xs text-muted-foreground font-mono overflow-hidden text-ellipsis whitespace-nowrap">
                                                {entry.entity_id || '—'}
                                            </TableCell>

                                            {/* Description */}
                                            <TableCell className="text-sm overflow-hidden text-ellipsis whitespace-nowrap">
                                                {entry.description || '—'}
                                            </TableCell>

                                            {/* Date */}
                                            <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                                                <span className="flex items-center gap-1.5">
                                                    <Clock className="h-3 w-3 shrink-0" />
                                                    {entry.created_at
                                                        ? format(new Date(entry.created_at), 'MMM dd, yyyy HH:mm')
                                                        : 'N/A'}
                                                </span>
                                            </TableCell>
                                        </motion.tr>
                                    )
                                })}
                            </motion.tbody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Pagination */}
            {pagination && (
                <DataPagination
                    page={listState.page}
                    totalPages={pagination.pages ?? 1}
                    onPageChange={listState.setPage}
                    totalItems={pagination.total}
                    perPage={listState.perPage}
                />
            )}
        </motion.div>
    )
}
